import type { EditForm } from '@/hooks/users/useUserEdit';

import React from 'react';
import { Button, Input, Modal, ModalBody, ModalContent, ModalFooter, ModalHeader, Select, SelectItem } from '@heroui/react';

interface EditUserModalProps {
  isOpen: boolean;
  onOpenChange: () => void;
  editForm: EditForm;
  setEditForm: React.Dispatch<React.SetStateAction<EditForm>>;
  isSaving: boolean;
  onSave: () => void;
}

export function EditUserModal({ isOpen, onOpenChange, editForm, setEditForm, isSaving, onSave }: EditUserModalProps) {
  const handleKeyPress = React.useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Enter' && !isSaving && editForm.first_name.trim() && editForm.last_name.trim()) {
        onSave();
      }
    },
    [onSave, isSaving, editForm.first_name, editForm.last_name]
  );

  return (
    <Modal isOpen={isOpen} placement="center" onOpenChange={onOpenChange}>
      <ModalContent>
        {(onClose) => (
          <>
            <ModalHeader className="flex flex-col gap-1">Edit user</ModalHeader>
            <ModalBody>
              <div className="flex flex-col gap-3" onKeyDown={handleKeyPress}>
                <Input
                  label="First name"
                  size="sm"
                  value={editForm.first_name}
                  onValueChange={(v) => setEditForm((f: EditForm) => ({ ...f, first_name: v }))}
                />
                <Input
                  label="Last name"
                  size="sm"
                  value={editForm.last_name}
                  onValueChange={(v) => setEditForm((f: EditForm) => ({ ...f, last_name: v }))}
                />
                <Input label="Email" size="sm" type="email" value={editForm.email} isReadOnly />
                <Input
                  description="Leave empty for unlimited or global default"
                  label="AI daily request limit"
                  size="sm"
                  type="number"
                  value={editForm.ai_daily_request_limit}
                  onValueChange={(value) => setEditForm((f: EditForm) => ({ ...f, ai_daily_request_limit: value }))}
                />
                <Select
                  label="Plan"
                  selectedKeys={[editForm.plan_slug]}
                  size="sm"
                  onSelectionChange={(keys) => {
                    const value = Array.from(keys)[0];

                    if (value === 'starter' || value === 'pro' || value === 'enterprise') {
                      setEditForm((f: EditForm) => ({ ...f, plan_slug: value }));
                    }
                  }}
                >
                  <SelectItem key="starter">Starter (1 workspace)</SelectItem>
                  <SelectItem key="pro">Pro (3 workspaces)</SelectItem>
                  <SelectItem key="enterprise">Enterprise (unlimited)</SelectItem>
                </Select>
              </div>
            </ModalBody>
            <ModalFooter>
              <Button variant="light" onPress={onClose}>
                Cancel
              </Button>
              <Button color="primary" isLoading={isSaving} onPress={onSave}>
                Save
              </Button>
            </ModalFooter>
          </>
        )}
      </ModalContent>
    </Modal>
  );
}
